/**
 * A keyboard-wedge scanner sends key positions, not characters, so with the OS keyboard in Korean mode a
 * QWERTY barcode arrives as Hangul [SSD-7050]. This maps the Korean 2-set (dubeolsik) layout back to the
 * keys that produced it. Korean mode loses letter case for every key but the seven shifted jamo, so
 * letters come back upper case: shelf codes are stored upper case and carrier waybills are printed so.
 */

/** Each compatibility jamo with the QWERTY keys that type it; compound jamo take two keys. */
const JAMO_KEYS: Record<string, string> = Object.fromEntries(
  [
    'ㄱR ㄲR ㄳRT ㄴS ㄵSW ㄶSG ㄷE ㄸE ㄹF ㄺFR ㄻFA ㄼFQ ㄽFT ㄾFX ㄿFV ㅀFG ㅁA ㅂQ ㅃQ ㅄQT ㅅT ㅆT ㅇD ㅈW ㅉW ㅊC',
    'ㅋZ ㅌX ㅍV ㅎG ㅏK ㅐO ㅑI ㅒO ㅓJ ㅔP ㅕU ㅖP ㅗH ㅘHK ㅙHO ㅚHL ㅛY ㅜN ㅝNJ ㅞNP ㅟNL ㅠB ㅡM ㅢML ㅣL',
  ]
    .join(' ')
    .split(' ')
    .map((pair) => [pair.slice(0, 1), pair.slice(1)]),
);

/** The Unicode syllable block's own ordering of initials, medials and finals. */
const INITIALS = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';
const MEDIALS = 'ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ';
const FINALS = ['', ...'ㄱㄲㄳㄴㄵㄶㄷㄹㄺㄻㄼㄽㄾㄿㅀㅁㅂㅄㅅㅆㅇㅈㅊㅋㅌㅍㅎ'];

const SYLLABLE_FIRST = 0xac00;
const SYLLABLE_LAST = 0xd7a3;
const MEDIAL_COUNT = 21;
const FINAL_COUNT = 28;

const keysOfSyllable = (codePoint: number): string => {
  const offset = codePoint - SYLLABLE_FIRST;
  const initial = INITIALS[Math.floor(offset / (MEDIAL_COUNT * FINAL_COUNT))] ?? '';
  const medial = MEDIALS[Math.floor(offset / FINAL_COUNT) % MEDIAL_COUNT] ?? '';
  const final = FINALS[offset % FINAL_COUNT] ?? '';
  return [initial, medial, final].map((jamo) => JAMO_KEYS[jamo] ?? '').join('');
};

/** True when the text holds anything a Korean-mode keyboard typed. */
export const hasHangul = (text: string): boolean => /[ㄱ-ㅣ가-힣]/.test(text);

/** The text with every Hangul syllable and jamo replaced by the QWERTY keys that typed it; the rest is untouched. */
export const toScanAscii = (raw: string): string => {
  // NFC first, so Hangul that arrived as conjoining jamo is matched as the syllable it spells.
  const composed = raw.normalize('NFC');
  if (!hasHangul(composed)) {
    return raw;
  }
  return Array.from(composed, (char) => {
    const codePoint = char.codePointAt(0) ?? 0;
    if (codePoint >= SYLLABLE_FIRST && codePoint <= SYLLABLE_LAST) {
      return keysOfSyllable(codePoint);
    }
    return JAMO_KEYS[char] ?? char;
  }).join('');
};
